import { formatFolderEntityName } from '@features/st-folder-viewer/st-folder-display'
import { ReactNode } from 'react'
import { useClientLoginFlow } from './use-client-login-flow'
import { ClientLoginLoadingCard, ClientLoginPage, QuestionnaireNotFound } from './client-login-page'
import { match } from '@st/pattern'
import { useSelect } from '~/store'
import { OrganizationLogo } from '@features/organizations'
import { User } from '@features/auth/auth-client-adapter'
import { BasicUser, Organization } from '@st/sdk'
import { Button, FullPageCard } from '@st/theme'
import { inflect } from '@st/util/inflect'
import { selFolderState } from '@features/st-folder-viewer/st-folder-selectors'

export function ClientOrganizerLoginPage({
  folderId,
  children
}: {
  folderId: string
  children: ReactNode
}) {
  const { state, send } = useClientLoginFlow()
  const folderState = useSelect('stFolder', selFolderState)
  const unauthorizedFolder = useSelect('stFolder', (stFolder) =>
    stFolder.status == 'unauthorized' ? stFolder.folderState : null
  )

  if (!state) {
    return <ClientLoginLoadingCard />
  }

  return match(state)
    .with({ status: 'idle' }, () => <ClientLoginLoadingCard />)
    .with({ status: 'loading' }, () => <ClientLoginLoadingCard />)
    .with({ status: 'finishing' }, () => <ClientLoginLoadingCard />)
    .with({ status: 'notFound' }, () => <QuestionnaireNotFound folderId={folderId} />)
    .with({ status: 'loggedIn' }, ({ currentUser }) => {
      if (unauthorizedFolder) {
        return (
          <UnauthorizedAccess
            organization={unauthorizedFolder.organization}
            currentUser={currentUser}
            allowedUsers={unauthorizedFolder.users}
            onClickLogout={() => send({ type: 'logoutRequested' })}
          />
        )
      }

      if (!folderState) {
        return <ClientLoginLoadingCard />
      }

      return <>{children}</>
    })
    .otherwise(() => {
      const publicFolder = match(state)
        .with({ flow: { kind: 'organizer' } }, ({ flow }) => flow.publicFolder)
        .otherwise(() => undefined)
      if (!publicFolder) {
        return <ClientLoginLoadingCard />
      }
      const title = formatFolderEntityName(publicFolder.entities, 'client')
      const subtitle = `${publicFolder.year} Tax Organizer`

      return (
        <ClientLoginPage
          organization={publicFolder.organization}
          title={title}
          subtitle={subtitle}
          possibleUsers={publicFolder.users}
          state={state}
          send={send}
        >
          {children}
        </ClientLoginPage>
      )
    })
}

function UnauthorizedAccess({
  organization,
  currentUser,
  allowedUsers,
  onClickLogout
}: {
  organization: Pick<Organization, 'name' | 'logo'>
  currentUser: Pick<User, 'email' | 'provider'>
  allowedUsers: Array<Pick<BasicUser, 'email'>>
  onClickLogout: () => void
}) {
  const allowedEmails = allowedUsers.map((u) => u.email)
  const logo = organization.logo
  const unauthorizedMessage = match(currentUser)
    .with({ provider: 'karbon' }, () => (
      <p>
        You are signed in with Karbon as {currentUser.email}, but that account is unauthorized to
        view this organizer.
      </p>
    ))
    .otherwise(() => <p>Your email {currentUser.email} is unauthorized to view this organizer.</p>)

  return (
    <FullPageCard
      footer={
        <p className="mt-10 text-center text-sm text-gray-500">
          Powered by StanfordTax, Inc.
          <br /> 222 Broadway, New York, 10032
        </p>
      }
    >
      <div className="flex flex-col items-center gap-1">
        {logo && <OrganizationLogo src={logo} className="mx-auto h-10 w-auto" />}

        <h3 className="text-center text-xl font-bold leading-9 tracking-tight text-gray-900">
          {organization.name}
        </h3>

        <div className="mt-12 text-center text-lg">{unauthorizedMessage}</div>
        <Button className="mt-4" variant="primary" onClick={onClickLogout}>
          Logout
        </Button>
        <p className="mt-6 text-center text-sm">
          The following {inflect(allowedEmails.length, 'email')} can access this questionnaire:{' '}
          {allowedEmails.join(', ')}
        </p>
      </div>
    </FullPageCard>
  )
}
